import { videoApiConfig } from "./authConfig";
import { videoClient } from "../api/httpClients/videoClient";

/**
 * Get client access URI for WebSocket connection
 * @param {string} accessToken - User access token
 * @returns {Promise<Object>} Response with WebSocket URL
 */
export const getClientAccessUri = async (accessToken) => {
  try {
    // accessToken arg kept for backwards compatibility; token is attached centrally.
    const response = await videoClient.post(
      videoApiConfig.endpoints.getClientAccessUri,
      {}
    );

    console.log("Get Client Access URI Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting client access URI:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

/**
 * Get videos for a specific study
 * @param {string} studyId - Study ID
 * @param {string} accessToken - User access token
 * @returns {Promise<Object>} Response with video list
 */
export const getVideos = async (studyId, accessToken) => {
  try {
    const response = await videoClient.post(
      videoApiConfig.endpoints.getVideos,
      {
        studyId: studyId,
      }
    );

    console.log("Get Videos Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error getting videos:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

/**
 * Delete videos
 * @param {string} studyId - Study ID (can be empty string for intro videos)
 * @param {string[]} vids - Array of video IDs to delete
 * @param {string} accessToken - User access token
 * @returns {Promise<Object>} Response from delete operation
 */
export const deleteVideos = async (studyId, vids, accessToken) => {
  try {
    const response = await videoClient.post(
      videoApiConfig.endpoints.deleteVideos,
      {
        ...(studyId ? { studyId: studyId } : {}),
        vids: vids,
      }
    );

    console.log("Delete Videos Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error deleting videos:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

/**
 * WebSocket connection manager for video events
 */
export class VideoWebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.onVideoReceived = null;
    this.onConnectionStatus = null;
  }

  /**
   * Connect to WebSocket
   * @param {string} wsUrl - WebSocket URL
   * @param {Function} onVideoReceived - Callback for new video events
   * @param {Function} onConnectionStatus - Callback for connection status
   */
  async connect(wsUrl, onVideoReceived, onConnectionStatus) {
    // Disconnect any existing connection first
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log("Disconnecting existing WebSocket connection");
      this.ws.close();
    }

    this.onVideoReceived = onVideoReceived;
    this.onConnectionStatus = onConnectionStatus;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log("Video WebSocket connected");
        this.reconnectAttempts = 0;
        if (this.onConnectionStatus) {
          this.onConnectionStatus("connected");
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Video WebSocket message received:", data);

          // Handle VIDEO_UPLOAD_COMPLETE_EVENT messages
          if (
            data.MessageType === "VIDEO_UPLOAD_COMPLETE_EVENT" &&
            this.onVideoReceived
          ) {
            this.onVideoReceived(data);
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      this.ws.onclose = (event) => {
        console.log("Video WebSocket closed:", event.code, event.reason);
        if (this.onConnectionStatus) {
          this.onConnectionStatus("disconnected");
        }
        this.attemptReconnect(wsUrl);
      };

      this.ws.onerror = (error) => {
        console.error("Video WebSocket error:", error);
        if (this.onConnectionStatus) {
          this.onConnectionStatus("error");
        }
      };
    } catch (error) {
      console.error("Error connecting to Video WebSocket:", error);
      throw error;
    }
  }

  /**
   * Attempt to reconnect to WebSocket
   * @param {string} wsUrl - WebSocket URL
   */
  attemptReconnect(wsUrl) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(
        `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`
      );

      setTimeout(() => {
        this.connect(wsUrl, this.onVideoReceived, this.onConnectionStatus);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error("Max reconnection attempts reached");
      if (this.onConnectionStatus) {
        this.onConnectionStatus("failed");
      }
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Get connection status
   * @returns {string} Connection status
   */
  getConnectionStatus() {
    if (!this.ws) return "disconnected";
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return "connecting";
      case WebSocket.OPEN:
        return "connected";
      case WebSocket.CLOSING:
        return "closing";
      case WebSocket.CLOSED:
        return "disconnected";
      default:
        return "unknown";
    }
  }
}
